import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import {useListController} from 'hooks/useListController'
import {IProductPromotion} from 'models/ActiveManagement'
import {categoryList} from 'constants/common'
import React, {useState} from 'react'
import {
 fetchProductPromotions,
 updateCampaignApi,
} from 'utils/api/auth/productPromotionService'
import {useTranslation} from 'react-i18next'
import KTTable from 'helpers/ui-components/KTTable'
import {useNavigate} from 'react-router-dom'
import KTPagination from 'helpers/ui-components/KTPagination'
import {ColumnProps} from 'models/Common'
import Spinner from 'layout/common/Spinner'

const RecommendedPromotion = () => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const [selectedCategory, setSelectedCategory] =
  useState('')
 const [loader, setLoader] = useState(false)
 const {
  data: tableData,
  total: totalRecord,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  setSearchTerm,
  applyFilters,
  reload,
 } = useListController<IProductPromotion>({
  fetcher: fetchProductPromotions,
  initialPageSize: 10,
  setLoader,
 })

 const columns: Array<ColumnProps<IProductPromotion>> = [
  {
   key: 'id',
   title: t('productPromotion.productTable.srNo'),
   className: 'w-[70px] text-center',
   render: (_column, _record, index?: number) => {
    if (index === undefined) return '-'

    return (currentPage - 1) * itemsPerPage + index + 1
   },
  },
  {
   key: 'campaign_name',
   title: t('productPromotion.productTable.promotionName'),
  },
  {
   key: 'product_name',
   title: (
    <div className='text-left'>
     {t('productPromotion.productTable.skuSizeName')}
     <br />
     {t('productPromotion.productTable.skuSize')}
    </div>
   ),
   render: (_, record) => (
    <>
     <p className='text-[#33393D]'>{record.product_name}</p>
     <p className='text-xs text-[#5A6066]'>
      {record.product_variant_size}
     </p>
    </>
   ),
  },
  {
   key: 'created_at',
   title: t('productPromotion.productTable.createDate'),
   render: (_, record) => {
    if (!record.created_at) return '-'

    const date = new Date(record.created_at)

    return date
     .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
     })
     .replace(/ /g, '-')
   },
  },

  {
   key: 'campaign_variant_count',
   title: t('productPromotion.productTable.language'),
  },
  {
   key: 'is_active',
   title: t('productPromotion.productTable.active'),
   className: 'text-center',
   render: (_, record) => (
    <label className='inline-flex items-center cursor-pointer'>
     <input
      type='checkbox'
      checked={record.is_active}
      className='sr-only peer'
      onChange={async () => {
       const newStatus = !record.is_active
       const payload = {id: record.id, is_active: newStatus}
       const res = await updateCampaignApi(payload)

       if (res?.data) {
        void reload()
       }
      }}
     />
     <div className="w-10 h-5 bg-[#E6EBF0] border border-[#CED8E0] peer-focus:outline-none rounded-full relative transition-colors after:content-[''] after:absolute after:left-[2px] after:top-1/2 after:-translate-y-1/2 after:h-4 after:w-4 after:rounded-full after:bg-black after:transition-all peer-checked:bg-[#4E83E4] peer-checked:border-transparent peer-checked:after:bg-white peer-checked:after:translate-x-5" />
    </label>
   ),
  },
  {
   key: 'action',
   title: t('productPromotion.productTable.action'),
   className: 'text-center',
   render: (_, record) => {
    return (
     <div
      className='flex items-center justify-center w-full h-full'
      onClick={() =>
       navigate(`create-promotion/${record.id}`)
      }
     >
      <div className='!px-4 !py-1.5 text-[#4E83E4] border border-[#4E83E4] !rounded-lg cursor-pointer'>
       {t('productPromotion.productTable.viewDetails')}
      </div>
     </div>
    )
   },
  },
 ]

 const applyCategoryFilter = (type: string) => {
  setSelectedCategory(type)
  const filters: Record<string, boolean> = {}
  if (type === 'Inactive') {
   filters.is_active = false
  } else if (type === 'Active') {
   filters.is_active = true
  }
  applyFilters(filters)
 }
 return (
  <>
   {loader && (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/30'>
     <Spinner />
    </div>
   )}
   <>
    <p style={{fontSize: 16, fontWeight: '600'}}>
     {t('productPromotion.promotionMessage')}
    </p>
    <div
     className='mt-5 flex items-center gap-4'
     style={{
      padding: 8,
      borderRadius: 8,
      backgroundColor: '#F5F8FA',
     }}
    >
     <div
      className='flex-1'
      style={{backgroundColor: 'white'}}
     >
      <KTSearchBox
       label={t('productPromotion.productTable.search')}
       placeholder={t(
        'productPromotion.productTable.searchPlaceholder'
       )}
       setSearchTerm={setSearchTerm}
      />
     </div>

     <div className='flex items-center'>
      <div className={`text-[#33393D] text-sm mr-2`}>
       {t('common.quickFilters')}
      </div>
      {categoryList.map((val, key) => (
       <div
        key={key}
        onClick={() => applyCategoryFilter(val.key)}
        className={`${
         selectedCategory === val.key
          ? '!bg-[#4E83E4] text-white border-[#4E83E4]'
          : ''
        } text-center py-1.5 px-3 bg-white rounded-lg text-sm text-[#33393D] border border-[#CED8E0] hover:bg-[#4E83E4] hover:text-white cursor-pointer mr-2 w-[90px]`}
       >
        {val.value}
       </div>
      ))}
     </div>
    </div>
    <div className='mt-6'>
     <KTTable
      data={tableData}
      columns={columns}
      headerClass='bg-[#80868C]'
      rowClass='even:bg-[#F5F8FA]'
      colClass='!text-sm'
      showPagination={false}
     />
     <div
      onClick={() => {
       navigate('create-promotion')
      }}
      className='inline-flex items-center gap-2 bg-[#5B7EE5]
               text-white font-semibold text-sm px-4 py-2 rounded-lg mt-5 cursor-pointer'
     >
      <span className='text-lg leading-none'>+</span>
      {t(
       'productPromotion.productTable.createRecommendation'
      )}
     </div>
    </div>

    <div className='mt-4 flex flex-col'>
     <KTPagination
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalRecord={totalRecord}
      onPageChange={setCurrentPage}
      setItemsPerPage={(size) => {
       setItemsPerPage(size)
       setCurrentPage(1)
      }}
     />
    </div>
   </>
  </>
 )
}

export default RecommendedPromotion
